import { SubmissionMethod } from "encamp-prisma/generated/browser";

/**
 * Helpers for the "Generator Status Change Notification" submission type, which
 * staff bulk-create from the Potential Exceedances dashboard
 * (`/staff/generator-status-changes`).
 *
 * The submission *type* is always the same; only the submission *method* varies
 * by the facility's state. Federally a generator status change is filed through
 * RCRAInfo's myRCRAid module (EPA Form 8700-12 Site ID form); a handful of
 * states require it through their own portal instead.
 */

/**
 * Default method for a generator status change notification: RCRAInfo myRCRAid.
 * Applies to every state that does not have a state-specific override below.
 */
export const GENERATOR_STATUS_CHANGE_DEFAULT_SUBMISSION_METHOD =
  SubmissionMethod.RCRAINFO_MYRCRAID_MODULE;

/**
 * State-specific overrides for where a generator status change notification is
 * filed. Keyed by two-letter state code. Any state absent here uses the federal
 * RCRAInfo myRCRAid default above.
 *
 * Source of truth: the waste team's "Notifications Hub" → "State-by-State
 * Guidance" database in Notion, whose "Utilizes MyRCRAid" column marks which
 * states file notifications through their own portal instead of RCRAInfo:
 * https://app.notion.com/p/2e82f9edc08f80a7acfbd7e9a6138247
 *
 * The entries below are the states verified against that database as filing
 * through a portal we model in `SubmissionMethod`:
 *   - AZ → MyDEQ
 *   - TX → STEERS ("preferred for simple modifications, e.g. generator status updates")
 *   - KY → Gateway eForms
 *
 * The remaining "Utilizes MyRCRAid: NO" states either file on paper or use a
 * portal we don't yet model, so they stay on the overridable default rather than
 * being silently mapped to the wrong method — reconcile the full list with the
 * waste team before enabling unattended auto-assignment for them.
 */
export const GENERATOR_STATUS_CHANGE_SUBMISSION_METHOD_BY_STATE: Partial<
  Record<string, SubmissionMethod>
> = {
  AZ: SubmissionMethod.AZ_MYDEQ_PORTAL,
  TX: SubmissionMethod.TX_STEERS_PORTAL,
  KY: SubmissionMethod.KY_GATEWAY_PORTAL,
};

/**
 * Returns the submission method a generator status change notification should
 * use for a facility in the given state, falling back to the federal default.
 */
export function getGeneratorStatusChangeSubmissionMethod(state?: string | null): SubmissionMethod {
  if (!state) {
    return GENERATOR_STATUS_CHANGE_DEFAULT_SUBMISSION_METHOD;
  }
  return (
    GENERATOR_STATUS_CHANGE_SUBMISSION_METHOD_BY_STATE[state.toUpperCase()] ??
    GENERATOR_STATUS_CHANGE_DEFAULT_SUBMISSION_METHOD
  );
}

const MONTH_ABBREVIATIONS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const GENERATOR_STATUS_CHANGE_SUBMISSION_TITLE = "Generator Status Change Notification";

// Dates are read in UTC so date-only `periodStart` values do not drift across time zones.
export function buildGeneratorStatusChangeSubmissionTitle(
  periodStart?: Date | string | null
): string {
  if (!periodStart) {
    return GENERATOR_STATUS_CHANGE_SUBMISSION_TITLE;
  }
  const date = typeof periodStart === "string" ? new Date(periodStart) : periodStart;
  if (Number.isNaN(date.getTime())) {
    return GENERATOR_STATUS_CHANGE_SUBMISSION_TITLE;
  }
  const label = `${MONTH_ABBREVIATIONS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
  return `${GENERATOR_STATUS_CHANGE_SUBMISSION_TITLE} (Review of ${label} Data)`;
}
